/**
 * PSP/CRE Tool v2.0 - Threat Assessment Constants
 * 
 * Purpose: Define threat types, enemy capabilities, and their impacts
 * on casualty rates, evacuation, and medical planning.
 * 
 * Author: Dr. Tatar Radu MD
 * Institution: Romanian Military Medical Institute
 * Date: January 18, 2025
 * Version: 2.1 - CRIT-004 FIX: Added missing casualty_modifier to LIMITED_FPV
 * 
 * Changes from v2.0:
 * - FIXED CRIT-004: LIMITED_FPV was missing casualty_modifier property
 *   - UI showed "undefinedx" instead of multiplier value
 *   - Added casualty_modifier: 1.2 (between RECON 1.1 and CONTESTED 1.4)
 * 
 * Changes from v1.0:
 * - Added FPV_INJURY_PATTERNS with body region distribution
 * - Added quarterly drone trends Q1 2022 - Q4 2024
 * - Added LOITERING_MUNITIONS categories
 * - Added MEDICAL_COUNTER_UAS recommendations
 * - Added specialty surgical demand modifiers for drones
 * - Added threat score calculation
 * - Added evacuation recommendation generator
 * - Enhanced calculator integration functions
 * 
 * References:
 * - NATO AJMedP-1 Allied Joint Medical Planning Doctrine
 * - AJP-4.10 Allied Joint Doctrine for Medical Support
 * - AMedP-1.5 NATO Medical Treatment Facilities
 * - Ukraine 2022-2024 Conflict Data
 * - SABERS/Dupuy Historical Casualty Research
 * 
 * Usage:
 * import { 
 *   THREAT_TYPES, 
 *   ENEMY_AD_LEVELS, 
 *   IED_THREAT_LEVELS,
 *   getThreatModifiers 
 * } from '../constants/threatAssessment';
 */

// ============================================================================
import { STATUS_ICONS, TACTICAL_ICONS, TRANSPORT_ICONS } from '../utils/icons';
// THREAT TYPES
// Overall character of the threat environment
// ============================================================================

export const THREAT_TYPES = {
  CONVENTIONAL: {
    value: 'CONVENTIONAL',
    label: 'Conventional Warfare',
    shortLabel: 'CONV',
    description: 'State-on-state conventional military operations with formed units',
    icon: TACTICAL_ICONS.ATTACK || '⚔️',
    color: 'error',
    
    // Casualty profile
    casualty_modifier: 1.0,  // Base rate
    kia_ratio: 0.20,         // 20% of casualties are KIA
    wia_distribution: {
      T1: 0.15,  // Immediate (urgent surgical)
      T2: 0.25,  // Delayed (surgical within 4-6 hrs)
      T3: 0.30,  // Minimal (ambulatory)
      T4: 0.05   // Expectant
    },
    
    // Wound patterns (affects surgical needs)
    wound_patterns: {
      penetrating: 0.45,      // Gunshot, shrapnel
      blast: 0.35,            // Artillery, mortars, mines
      blunt: 0.10,            // Vehicle accidents, falls
      burn: 0.05,             // Thermal, chemical
      other: 0.05
    },
    
    // Evacuation impacts
    evacuation_factors: {
      medevac_risk_modifier: 1.0,
      ground_evac_risk_modifier: 1.0,
      route_interdiction_probability: 0.10  // 10% chance of route attack
    },
    
    // DNBI modifier
    dnbi_modifier: 1.0,
    
    // Medical planning notes
    notes: 'Standard NATO planning factors apply. Expect high surgical workload from penetrating trauma.'
  },
  
  ASYMMETRIC: {
    value: 'ASYMMETRIC',
    label: 'Asymmetric/Counterinsurgency',
    shortLabel: 'ASYM',
    description: 'Operations against irregular forces, insurgents, or terrorists',
    icon: '💣',
    color: 'warning',
    
    casualty_modifier: 0.7,  // Lower overall rate but different patterns
    kia_ratio: 0.15,         // Lower KIA but more complex injuries
    wia_distribution: {
      T1: 0.20,  // More urgent cases (IED blast injuries)
      T2: 0.30,
      T3: 0.25,
      T4: 0.05
    },
    
    wound_patterns: {
      penetrating: 0.25,      // Less direct fire
      blast: 0.55,            // IEDs dominant
      blunt: 0.10,
      burn: 0.05,
      other: 0.05
    },
    
    evacuation_factors: {
      medevac_risk_modifier: 0.8,   // Lower AD threat
      ground_evac_risk_modifier: 1.3, // Higher IED/ambush risk
      route_interdiction_probability: 0.25
    },
    
    dnbi_modifier: 1.2,  // Often worse sanitary conditions
    
    notes: 'Expect higher proportion of blast injuries. IED patterns require specialized surgical capability. Mental health burden higher.'
  },
  
  HYBRID: {
    value: 'HYBRID',
    label: 'Hybrid Warfare',
    shortLabel: 'HYBRID',
    description: 'Combined conventional and irregular threats with deniable actors',
    icon: '🎭',
    color: 'secondary',
    
    casualty_modifier: 1.2,  // Higher due to unpredictability
    kia_ratio: 0.18,
    wia_distribution: {
      T1: 0.18,
      T2: 0.28,
      T3: 0.28,
      T4: 0.06
    },
    
    wound_patterns: {
      penetrating: 0.35,
      blast: 0.40,
      blunt: 0.10,
      burn: 0.10,
      other: 0.05
    },
    
    evacuation_factors: {
      medevac_risk_modifier: 1.2,
      ground_evac_risk_modifier: 1.2,
      route_interdiction_probability: 0.20
    },
    
    dnbi_modifier: 1.3,
    
    notes: 'Plan for both conventional mass casualty events AND persistent low-level attrition. Information environment affects mental health.'
  },
  
  PEACEKEEPING: {
    value: 'PEACEKEEPING',
    label: 'Peace Support Operations',
    shortLabel: 'PSO',
    description: 'Peacekeeping, monitoring, or stability operations',
    icon: '🕊️',
    color: 'info',
    
    casualty_modifier: 0.3,  // Much lower combat intensity
    kia_ratio: 0.10,
    wia_distribution: {
      T1: 0.10,
      T2: 0.20,
      T3: 0.50,  // More minor injuries
      T4: 0.02
    },
    
    wound_patterns: {
      penetrating: 0.20,
      blast: 0.15,
      blunt: 0.40,  // Accidents, civil unrest
      burn: 0.05,
      other: 0.20   // Medical emergencies
    },
    
    evacuation_factors: {
      medevac_risk_modifier: 0.5,
      ground_evac_risk_modifier: 0.7,
      route_interdiction_probability: 0.05
    },
    
    dnbi_modifier: 1.5,  // DNBI often exceeds battle casualties
    
    notes: 'DNBI typically exceeds battle casualties. Focus on force health protection and preventive medicine.'
  },
  
  HIGH_INTENSITY: {
    value: 'HIGH_INTENSITY',
    label: 'High-Intensity Peer Conflict',
    shortLabel: 'HIGH-INT',
    description: 'Near-peer or peer conflict with advanced threats (Ukraine 2022+ model)',
    icon: '🔥',
    color: 'error',
    
    casualty_modifier: 1.8,  // Significantly higher rates
    kia_ratio: 0.25,         // Higher lethality
    wia_distribution: {
      T1: 0.20,
      T2: 0.25,
      T3: 0.25,
      T4: 0.08   // More expectant due to severity
    },
    
    wound_patterns: {
      penetrating: 0.30,
      blast: 0.50,            // Massive artillery, drones
      blunt: 0.05,
      burn: 0.10,
      other: 0.05
    },
    
    evacuation_factors: {
      medevac_risk_modifier: 1.8,   // High AD threat
      ground_evac_risk_modifier: 1.5, // Route interdiction
      route_interdiction_probability: 0.35
    },
    
    dnbi_modifier: 1.5,
    
    notes: 'Based on Ukraine 2022-2024 data. Expect MEDEVAC restrictions due to AD. Plan for mass casualties. Drone threat affects all rear areas.'
  }
};

// ============================================================================
// ENEMY AIR DEFENSE LEVELS
// Affects MEDEVAC helicopter operations
// ============================================================================

export const ENEMY_AD_LEVELS = {
  NONE: {
    value: 'NONE',
    label: 'No Air Defense Threat',
    shortLabel: 'NONE',
    description: 'Enemy has no effective air defense capability',
    icon: STATUS_ICONS.GREEN_CHECK || '✅',
    color: 'success',
    
    // MEDEVAC impacts
    medevac_availability: 1.0,      // Full availability
    medevac_risk_per_sortie: 0.001, // 0.1% loss risk per sortie
    golden_hour_achievable: true,
    day_night_restriction: 'NONE',
    altitude_restriction: 'NONE',
    
    // Recommended procedures
    procedures: {
      flight_profile: 'DIRECT',
      escort_required: false,
      route_coordination: 'MINIMAL',
      ima_use: false  // Instrument Meteorological Approach
    },
    
    notes: 'Full MEDEVAC operations. Golden hour achievable for most casualties.'
  },
  
  LIMITED: {
    value: 'LIMITED',
    label: 'Limited Air Defense',
    shortLabel: 'LIMITED',
    description: 'Small arms, some MANPADS, no integrated system',
    icon: STATUS_ICONS.YELLOW_WARNING || '⚠️',
    color: 'warning',
    
    medevac_availability: 0.90,
    medevac_risk_per_sortie: 0.02,  // 2% risk
    golden_hour_achievable: true,
    day_night_restriction: 'NIGHT_PREFERRED',
    altitude_restriction: 'LOW_LEVEL',
    
    procedures: {
      flight_profile: 'NOE',  // Nap-of-earth
      escort_required: false,
      route_coordination: 'STANDARD',
      ima_use: true
    },
    
    notes: 'MEDEVAC viable with tactical procedures. May need to avoid known threat areas.'
  },
  
  MODERATE: {
    value: 'MODERATE',
    label: 'Moderate Air Defense',
    shortLabel: 'MOD',
    description: 'Coordinated MANPADS, some radar-guided systems',
    icon: '🚨',
    color: 'warning',
    
    medevac_availability: 0.70,
    medevac_risk_per_sortie: 0.05,  // 5% risk
    golden_hour_achievable: false,  // Cannot guarantee
    day_night_restriction: 'NIGHT_ONLY',
    altitude_restriction: 'TERRAIN_MASKING',
    
    procedures: {
      flight_profile: 'NOE_TACTICAL',
      escort_required: true,
      route_coordination: 'DETAILED',
      ima_use: true
    },
    
    // Ground evacuation enhancement
    ground_evac_emphasis: 1.3,  // Increase ground assets by 30%
    
    notes: 'MEDEVAC restricted. Increase ground ambulance capability. Expect evacuation delays. Consider forward surgical capability.'
  },
  
  DENSE: {
    value: 'DENSE',
    label: 'Dense Air Defense',
    shortLabel: 'DENSE',
    description: 'Integrated air defense system with multiple layers',
    icon: '🛑',
    color: 'error',
    
    medevac_availability: 0.30,
    medevac_risk_per_sortie: 0.15,  // 15% risk
    golden_hour_achievable: false,
    day_night_restriction: 'SEVERELY_RESTRICTED',
    altitude_restriction: 'TERRAIN_MASKING_ONLY',
    
    procedures: {
      flight_profile: 'EMERGENCY_ONLY',
      escort_required: true,
      route_coordination: 'SEAD_REQUIRED',  // Suppression of Enemy AD
      ima_use: true
    },
    
    ground_evac_emphasis: 2.0,  // Double ground assets
    forward_surgical_required: true,
    
    notes: 'MEDEVAC severely restricted. Assume ground evacuation for planning. Deploy forward surgical teams (FST). Expect higher DOW rates.'
  },
  
  SOPHISTICATED: {
    value: 'SOPHISTICATED',
    label: 'Sophisticated/Layered AD',
    shortLabel: 'SOPH',
    description: 'Modern integrated AD with long-range systems (S-300/400 class)',
    icon: '⛔',
    color: 'error',
    
    medevac_availability: 0.10,  // Near zero
    medevac_risk_per_sortie: 0.30,  // 30% risk
    golden_hour_achievable: false,
    day_night_restriction: 'GROUND_ONLY',
    altitude_restriction: 'NO_FLIGHT',
    
    procedures: {
      flight_profile: 'PROHIBITED',
      escort_required: 'N/A',
      route_coordination: 'GROUND_ONLY',
      ima_use: 'N/A'
    },
    
    ground_evac_emphasis: 3.0,  // Triple ground assets
    forward_surgical_required: true,
    role_2e_forward: true,  // Push Role 2E forward
    
    // Casualty impact
    dow_modifier: 1.5,  // 50% higher DOW due to delayed evac
    
    notes: 'NO MEDEVAC. Ground evacuation only. Maximum forward surgical capability. Role 2E pushed forward. Plan for significantly higher DOW rates. (Ukraine 2022-2024 baseline)'
  }
};

// ============================================================================
// IED THREAT LEVELS
// Affects ground evacuation and casualty patterns
// ============================================================================

export const IED_THREAT_LEVELS = {
  NONE: {
    value: 'NONE',
    label: 'No IED Threat',
    shortLabel: 'NONE',
    description: 'No improvised explosive device threat',
    icon: STATUS_ICONS.GREEN_CHECK || '✅',
    color: 'success',
    
    casualty_modifier: 1.0,
    ground_evac_speed_modifier: 1.0,
    route_clearance_required: false,
    armored_ambulance_required: false,
    
    wound_pattern_adjustment: {
      blast: 0,  // No adjustment
      penetrating: 0
    },
    
    convoy_procedures: 'STANDARD',
    notes: 'Standard convoy operations. Full route availability.'
  },
  
  LOW: {
    value: 'LOW',
    label: 'Low IED Threat',
    shortLabel: 'LOW',
    description: 'Sporadic IED incidents, not systematic',
    icon: STATUS_ICONS.YELLOW_WARNING || '⚠️',
    color: 'info',
    
    casualty_modifier: 1.05,
    ground_evac_speed_modifier: 0.90,  // 10% slower
    route_clearance_required: false,
    armored_ambulance_required: false,
    
    wound_pattern_adjustment: {
      blast: 0.05,
      penetrating: -0.03
    },
    
    convoy_procedures: 'ENHANCED_AWARENESS',
    notes: 'Standard procedures with enhanced awareness. Avoid predictable patterns.'
  },
  
  MODERATE: {
    value: 'MODERATE',
    label: 'Moderate IED Threat',
    shortLabel: 'MOD',
    description: 'Regular IED activity on main routes',
    icon: '💣',
    color: 'warning',
    
    casualty_modifier: 1.15,
    ground_evac_speed_modifier: 0.75,  // 25% slower
    route_clearance_required: true,
    armored_ambulance_required: true,
    
    wound_pattern_adjustment: {
      blast: 0.15,
      penetrating: -0.10
    },
    
    // Evacuation time additions
    evacuation_delay_minutes: 15,  // Added to baseline
    
    convoy_procedures: 'ROUTE_CLEARANCE_REQUIRED',
    counter_ied_assets_required: true,
    
    notes: 'Route clearance required. Armored ambulances for ground evac. Expect evacuation delays.'
  },
  
  HIGH: {
    value: 'HIGH',
    label: 'High IED Threat',
    shortLabel: 'HIGH',
    description: 'Pervasive IED threat, multiple daily incidents',
    icon: '🚨',
    color: 'error',
    
    casualty_modifier: 1.30,
    ground_evac_speed_modifier: 0.50,  // 50% slower
    route_clearance_required: true,
    armored_ambulance_required: true,
    
    wound_pattern_adjustment: {
      blast: 0.25,
      penetrating: -0.15
    },
    
    evacuation_delay_minutes: 30,
    
    // Significant impact on DOW
    dow_modifier: 1.2,  // 20% higher DOW
    
    convoy_procedures: 'DELIBERATE_ROUTE_CLEARANCE',
    counter_ied_assets_required: true,
    alternate_routes_required: true,
    
    notes: 'Significant evacuation delays. Plan for higher DOW. Forward surgical capability critical. Blast injury surgical expertise required.'
  },
  
  EXTREME: {
    value: 'EXTREME',
    label: 'Extreme IED Threat',
    shortLabel: 'EXTREME',
    description: 'Routes effectively denied, only cleared corridors usable',
    icon: '⛔',
    color: 'error',
    
    casualty_modifier: 1.50,
    ground_evac_speed_modifier: 0.30,  // 70% slower
    route_clearance_required: true,
    armored_ambulance_required: true,
    
    wound_pattern_adjustment: {
      blast: 0.35,
      penetrating: -0.20
    },
    
    evacuation_delay_minutes: 60,
    
    dow_modifier: 1.4,  // 40% higher DOW
    
    convoy_procedures: 'CLEARED_CORRIDORS_ONLY',
    counter_ied_assets_required: true,
    alternate_routes_required: true,
    consider_air_evac_despite_risk: true,
    
    notes: 'Routes effectively denied. Consider MEDEVAC despite AD risk if IED risk higher. Forward surgical teams at battalion level. Mass casualty blast injury capability required.'
  }
};

// ============================================================================
// INSURGENT ACTIVITY LEVELS
// Affects rear area security and LOC
// ============================================================================

export const INSURGENT_ACTIVITY = {
  NONE: {
    value: 'NONE',
    label: 'No Insurgent Activity',
    shortLabel: 'NONE',
    description: 'No insurgent or irregular force activity',
    icon: STATUS_ICONS.GREEN_CHECK || '✅',
    color: 'success',
    
    rear_area_casualty_modifier: 1.0,
    loc_security_modifier: 1.0,
    base_security_requirement: 'STANDARD',
    
    support_unit_risk: 0.0,  // No added risk for support units
    
    notes: 'Rear areas secure. Standard security posture.'
  },
  
  SPORADIC: {
    value: 'SPORADIC',
    label: 'Sporadic Activity',
    shortLabel: 'SPORADIC',
    description: 'Occasional harassment, sabotage attempts',
    icon: STATUS_ICONS.YELLOW_WARNING || '⚠️',
    color: 'info',
    
    rear_area_casualty_modifier: 1.05,
    loc_security_modifier: 0.95,
    base_security_requirement: 'ENHANCED',
    
    support_unit_risk: 0.02,  // 2% added casualty risk
    
    notes: 'Minor threat to rear areas. Enhanced security for key facilities.'
  },
  
  MODERATE: {
    value: 'MODERATE',
    label: 'Moderate Activity',
    shortLabel: 'MOD',
    description: 'Regular small-scale attacks on convoys and installations',
    icon: '🎯',
    color: 'warning',
    
    rear_area_casualty_modifier: 1.15,
    loc_security_modifier: 0.85,
    base_security_requirement: 'HIGH',
    
    support_unit_risk: 0.05,
    
    convoy_escort_required: true,
    medical_facility_security_required: true,
    
    notes: 'Convoy escorts required. Medical facilities need dedicated security. Consider hardening fixed sites.'
  },
  
  HIGH: {
    value: 'HIGH',
    label: 'High Activity',
    shortLabel: 'HIGH',
    description: 'Frequent attacks, semi-organized resistance',
    icon: '🚨',
    color: 'error',
    
    rear_area_casualty_modifier: 1.30,
    loc_security_modifier: 0.70,
    base_security_requirement: 'MAXIMUM',
    
    support_unit_risk: 0.10,
    
    convoy_escort_required: true,
    medical_facility_security_required: true,
    alternate_loc_required: true,
    
    notes: 'Rear areas effectively contested. Medical facilities at risk. Dispersion and security critical.'
  },
  
  HEAVY: {
    value: 'HEAVY',
    label: 'Heavy Activity',
    shortLabel: 'HEAVY',
    description: 'Organized insurgency, areas of control',
    icon: '⛔',
    color: 'error',
    
    rear_area_casualty_modifier: 1.50,
    loc_security_modifier: 0.50,
    base_security_requirement: 'FORTIFIED',
    
    support_unit_risk: 0.15,
    
    convoy_escort_required: true,
    medical_facility_security_required: true,
    alternate_loc_required: true,
    consider_relocation: true,
    
    notes: 'Rear areas heavily contested. Consider relocating vulnerable facilities. Support units require combat capability.'
  }
};

// ============================================================================
// CBRN RISK LEVELS
// Chemical, Biological, Radiological, Nuclear threats
// ============================================================================

export const CBRN_RISK = {
  NONE: {
    value: 'NONE',
    label: 'No CBRN Risk',
    shortLabel: 'NONE',
    description: 'No chemical, biological, radiological, or nuclear threat',
    icon: STATUS_ICONS.GREEN_CHECK || '✅',
    color: 'success',
    
    cbrn_casualty_addition: 0,
    decontamination_required: false,
    special_treatment_required: false,
    ppe_level: 'STANDARD',
    
    medical_facility_impact: {
      capacity_reduction: 0,
      treatment_time_modifier: 1.0
    },
    
    notes: 'Standard medical operations. No CBRN precautions required.'
  },
  
  LOW: {
    value: 'LOW',
    label: 'Low CBRN Risk',
    shortLabel: 'LOW',
    description: 'Possible but unlikely CBRN use',
    icon: STATUS_ICONS.YELLOW_WARNING || '⚠️',
    color: 'info',
    
    cbrn_casualty_addition: 0,
    decontamination_required: false,
    special_treatment_required: false,
    ppe_level: 'MOPP_READY',  // MOPP gear available
    
    medical_facility_impact: {
      capacity_reduction: 0,
      treatment_time_modifier: 1.0
    },
    
    detection_assets_required: true,
    antidote_stocks_required: true,
    
    notes: 'Maintain CBRN detection. Stock antidotes. No active precautions but ready to implement.'
  },
  
  MODERATE: {
    value: 'MODERATE',
    label: 'Moderate CBRN Risk',
    shortLabel: 'MOD',
    description: 'Credible CBRN threat, possible limited use',
    icon: '☢️',
    color: 'warning',
    
    cbrn_casualty_addition: 0.05,  // 5% of force as CBRN casualties if used
    decontamination_required: true,
    special_treatment_required: true,
    ppe_level: 'MOPP_2',
    
    medical_facility_impact: {
      capacity_reduction: 0.15,  // 15% capacity lost to CBRN procedures
      treatment_time_modifier: 1.3  // 30% longer treatment times
    },
    
    detection_assets_required: true,
    antidote_stocks_required: true,
    decon_stations_required: true,
    collective_protection_required: true,
    
    // DOW impact
    dow_modifier: 1.2,  // Higher DOW if CBRN used
    
    notes: 'Active CBRN posture. Decontamination stations at MTFs. Treatment times increased. Plan for contaminated casualties.'
  },
  
  HIGH: {
    value: 'HIGH',
    label: 'High CBRN Risk',
    shortLabel: 'HIGH',
    description: 'CBRN use likely or has occurred',
    icon: '☢️',
    color: 'error',
    
    cbrn_casualty_addition: 0.15,  // 15% additional casualties
    decontamination_required: true,
    special_treatment_required: true,
    ppe_level: 'MOPP_4',
    
    medical_facility_impact: {
      capacity_reduction: 0.30,  // 30% capacity lost
      treatment_time_modifier: 1.6
    },
    
    detection_assets_required: true,
    antidote_stocks_required: true,
    decon_stations_required: true,
    collective_protection_required: true,
    specialized_treatment_required: true,
    
    dow_modifier: 1.5,
    
    // Evacuation restrictions
    contaminated_evac_procedures: true,
    clean_dirty_separation: true,
    
    notes: 'Full CBRN posture. Significant medical capacity reduction. Contaminated casualty handling procedures. Specialized CBRN medical treatment required.'
  }
};

// ============================================================================
// ARTILLERY/INDIRECT FIRE THREAT
// New threat category based on Ukraine lessons
// ============================================================================

export const ARTILLERY_THREAT = {
  LOW: {
    value: 'LOW',
    label: 'Low Artillery Threat',
    shortLabel: 'LOW',
    description: 'Limited enemy artillery capability',
    icon: '💥',
    color: 'info',
    
    casualty_modifier: 1.0,
    rear_area_threat: 0.05,  // 5% chance of rear area strikes
    mtf_dispersion_required: false,
    
    notes: 'Standard operations. Fixed MTF locations acceptable.'
  },
  
  MODERATE: {
    value: 'MODERATE',
    label: 'Moderate Artillery Threat',
    shortLabel: 'MOD',
    description: 'Significant enemy tube and rocket artillery',
    icon: '💥',
    color: 'warning',
    
    casualty_modifier: 1.2,
    rear_area_threat: 0.15,
    mtf_dispersion_required: true,
    
    // Impact on operations
    mass_casualty_probability: 0.10,  // Per day
    mtf_damage_probability: 0.05,     // Per day per MTF
    
    notes: 'MTF dispersion required. Plan for mass casualty events. Consider hardening or mobility.'
  },
  
  HIGH: {
    value: 'HIGH',
    label: 'High Artillery Threat',
    shortLabel: 'HIGH',
    description: 'Dense long-range artillery with precision capability',
    icon: '💥',
    color: 'error',
    
    casualty_modifier: 1.5,
    rear_area_threat: 0.30,
    mtf_dispersion_required: true,
    mtf_mobility_required: true,
    
    mass_casualty_probability: 0.25,
    mtf_damage_probability: 0.10,
    
    // Signature reduction
    signature_reduction_required: true,
    no_static_concentration: true,
    
    notes: 'Maximum dispersion. Mobile MTF capability. Avoid signature. Plan for MTF displacement. Ukraine 2022-2024 baseline.'
  },
  
  EXTREME: {
    value: 'EXTREME',
    label: 'Extreme Artillery Threat',
    shortLabel: 'EXTREME',
    description: 'Saturated battlefield with precision fires (Ukraine model)',
    icon: '💥',
    color: 'error',
    
    casualty_modifier: 2.0,
    rear_area_threat: 0.50,
    mtf_dispersion_required: true,
    mtf_mobility_required: true,
    underground_protection_recommended: true,
    
    mass_casualty_probability: 0.40,
    mtf_damage_probability: 0.20,
    
    signature_reduction_required: true,
    no_static_concentration: true,
    constant_displacement: true,
    
    // Medical planning impact
    forward_surgical_critical: true,
    evacuation_windows: true,  // Only evac during gaps
    
    notes: 'Constant threat to all echelons. Forward surgical mandatory. MTF must be mobile or protected. Plan for daily MASCAL events.'
  }
};

// ============================================================================
// DRONE THREAT (FPV/Reconnaissance)
// Critical modern threat from Ukraine lessons
// ============================================================================

export const DRONE_THREAT = {
  NONE: {
    value: 'NONE',
    label: 'No Drone Threat',
    shortLabel: 'NONE',
    description: 'Enemy has no drone capability',
    icon: STATUS_ICONS.GREEN_CHECK || '✅',
    color: 'success',
    
    casualty_modifier: 1.0,
    dow_modifier: 1.0,   // no drone threat: no effect (defined 2026-10-10)
    signature_discipline: 'STANDARD',
    electronic_protection: false,
    
    notes: 'Standard operations.'
  },
  
  RECONNAISSANCE_ONLY: {
    value: 'RECONNAISSANCE_ONLY',
    label: 'Reconnaissance Drones Only',
    shortLabel: 'RECON',
    description: 'Enemy uses drones for ISR but not attack',
    icon: '👁️',
    color: 'info',
    
    casualty_modifier: 1.1,  // Better targeting = more accurate fires
    // DOW modifier (defined 2026-10-10, Dr. Tatar: "we define it"). The code read this field for every level
    // and it was undefined, so the drone DOW effect was always 1.0. Recon drones do not attack evacuation: 1.0.
    dow_modifier: 1.0,
    signature_discipline: 'ENHANCED',
    electronic_protection: true,
    
    // Impact: enemy artillery more accurate
    artillery_accuracy_modifier: 1.3,
    
    notes: 'Camouflage critical. EW protection required. Dispersion.'
  },
  
  LIMITED_FPV: {
    value: 'LIMITED_FPV',
    label: 'Limited FPV Attack Drones',
    shortLabel: 'LTD FPV',
    description: 'Some FPV kamikaze drones in use',
    icon: TRANSPORT_ICONS.DRONE || '🎯',
    color: 'warning',
    
    // v2.1 FIX: Was missing - caused "undefinedx" in UI
    casualty_modifier: 1.2,  // Between RECON (1.1) and CONTESTED (1.4)
    // DOW modifier (PLACEHOLDER, 2026-10-10): between RECON (1.0) and CONTESTED (1.3); some evacuation vehicles are hit.
    dow_modifier: 1.1,
    
    signature_discipline: 'STRICT',
    electronic_protection: true,
    counter_uas_required: true,
    
    // Direct FPV casualties
    fpv_casualty_rate: 0.5,  // Per 1,000 per day
    
    // Target prioritization (what drones target)
    priority_targets: ['VEHICLES', 'POSITIONS', 'MOVEMENT'],
    
    notes: 'FPV threat to vehicles and positions. Counter-UAS required. Movement discipline critical.'
  },
  
  CONTESTED: {
    value: 'CONTESTED',
    label: 'Contested Drone Environment',
    shortLabel: 'CONTESTED',
    description: 'Both sides using attack drones actively',
    icon: TACTICAL_ICONS.ATTACK || '⚔️',
    color: 'warning',
    
    casualty_modifier: 1.4,
    // DOW modifier (2026-10-10): between LIMITED_FPV (1.1) and SATURATED (1.3, the designed maximum).
    // PLACEHOLDER: no published source. The planner can override it in Tab 2 (drone_dow_modifier), also by day.
    dow_modifier: 1.2,
    signature_discipline: 'MAXIMUM',
    electronic_protection: true,
    counter_uas_required: true,
    
    fpv_casualty_rate: 1.0,
    
    // Evacuation impact
    evacuation_by_vehicle_risk: 1.3,
    medevac_vehicle_losses: 0.05,  // 5% vehicle loss rate per week
    
    notes: 'Active FPV threat to evacuation. Consider patient transfer procedures. Armored ambulances critical.'
  },
  
  SATURATED: {
    value: 'SATURATED',
    label: 'Drone-Saturated Battlefield',
    shortLabel: 'SATURATED',
    description: 'Pervasive drone presence (Ukraine 2023-2024 model)',
    icon: '🔥',
    color: 'error',
    
    casualty_modifier: 1.8,
    signature_discipline: 'SURVIVAL',
    electronic_protection: true,
    counter_uas_required: true,
    hardened_positions_required: true,
    
    fpv_casualty_rate: 2.0,
    
    priority_targets: ['VEHICLES', 'POSITIONS', 'MOVEMENT', 'STATIC_ASSETS', 'MEDICAL_VEHICLES'],
    
    evacuation_by_vehicle_risk: 1.8,
    medevac_vehicle_losses: 0.15,
    
    // Operational changes required
    casualty_collection_procedures_modified: true,
    walking_wounded_emphasis: true,
    
    dow_modifier: 1.3,  // Higher DOW due to delayed evac
    
    notes: 'Extreme drone threat. Medical vehicles targeted. Consider underground/covered positions. Night evacuation. Walking wounded procedures. Ukraine 2024 baseline.'
  }
};

// ============================================================================
// FPV DRONE INJURY PATTERNS (NEW in v2.0)
// Based on Ukraine 2022-2024 medical data
// ============================================================================

export const FPV_INJURY_PATTERNS = {
  description: 'FPV drones cause distinctive injury patterns due to attack profile',
  
  // Attack profile: Usually diving attacks from above
  typical_attack_profile: {
    approach_angle: 'STEEP_DIVE',
    detonation_height: '1-2m above target',
    fragmentation_pattern: 'DOWNWARD_CONE',
    warhead_typical: '40mm grenade or PG-7 warhead'
  },
  
  // Body region distribution (differs from artillery)
  body_region_distribution: {
    head_neck: 0.35,      // 35% - MUCH higher than artillery (15%)
    thorax: 0.20,
    abdomen: 0.15,
    upper_extremity: 0.15,
    lower_extremity: 0.15
  },
  
  // Compared to artillery (for reference)
  artillery_body_region: {
    head_neck: 0.15,
    thorax: 0.20,
    abdomen: 0.20,
    upper_extremity: 0.20,
    lower_extremity: 0.25
  },
  
  // Injury severity
  severity_distribution: {
    T1_IMMEDIATE: 0.25,   // Higher T1 due to head/neck
    T2_DELAYED: 0.30,
    T3_MINIMAL: 0.25,
    T4_EXPECTANT: 0.10,
    KIA: 0.10
  },
  
  // Specialty surgical needs
  specialty_requirements: {
    neurosurgery: 0.28,      // Much higher than average
    maxillofacial: 0.25,     // Face/jaw injuries
    ophthalmology: 0.18,     // Eye injuries
    ent: 0.12,               // Ear injuries
    general_surgery: 0.30,
    orthopedic: 0.40,
    vascular: 0.20
  },
  
  // Psychological impact
  psychological_impact: {
    terror_effect: 'HIGH',
    inability_to_hide: true,
    constant_threat_perception: true,
    psych_casualty_modifier: 1.3
  },
  
  // Quarterly trends (Ukraine 2022-2024)
  quarterly_trends: {
    '2022_Q1': { fpv_percentage: 0.02, head_neck_rate: 0.20 },
    '2022_Q2': { fpv_percentage: 0.05, head_neck_rate: 0.22 },
    '2022_Q3': { fpv_percentage: 0.08, head_neck_rate: 0.25 },
    '2022_Q4': { fpv_percentage: 0.12, head_neck_rate: 0.28 },
    '2023_Q1': { fpv_percentage: 0.15, head_neck_rate: 0.30 },
    '2023_Q2': { fpv_percentage: 0.18, head_neck_rate: 0.32 },
    '2023_Q3': { fpv_percentage: 0.22, head_neck_rate: 0.33 },
    '2023_Q4': { fpv_percentage: 0.25, head_neck_rate: 0.34 },
    '2024_Q1': { fpv_percentage: 0.28, head_neck_rate: 0.35 },
    '2024_Q2': { fpv_percentage: 0.30, head_neck_rate: 0.35 },
    '2024_Q3': { fpv_percentage: 0.32, head_neck_rate: 0.35 },
    '2024_Q4': { fpv_percentage: 0.35, head_neck_rate: 0.35 }
  },
  
  medical_implications: [
    'Stock maxillofacial plating systems at Role 2E',
    'Ophthalmology capability forward',
    'TBI screening all drone casualties',
    'Hearing protection critical',
    'Anticipate higher neurosurgical demand'
  ]
};

// ============================================================================
// LOITERING MUNITIONS (NEW in v2.0)
// Different characteristics from FPV drones
// ============================================================================

export const LOITERING_MUNITIONS = {
  description: 'Loitering munitions (Lancet, Switchblade, etc.) - larger warheads',
  
  types: {
    SMALL: {
      examples: ['Switchblade 300', 'Warmate'],
      warhead_kg: 0.5,
      target_type: 'PERSONNEL',
      casualty_radius_m: 5,
      casualty_per_strike: 2
    },
    MEDIUM: {
      examples: ['Switchblade 600', 'Lancet-1'],
      warhead_kg: 3,
      target_type: 'VEHICLES',
      casualty_radius_m: 15,
      casualty_per_strike: 5
    },
    LARGE: {
      examples: ['Lancet-3', 'Shahed-136'],
      warhead_kg: 40,
      target_type: 'INFRASTRUCTURE',
      casualty_radius_m: 50,
      casualty_per_strike: 20
    }
  },
  
  medical_impact: {
    mass_casualty_potential: 'HIGH',
    burn_injury_rate: 0.25,
    fragmentation_injuries: 0.60,
    blast_injuries: 0.70,
    mtf_targeting_risk: 'MODERATE'
  },
  
  defensive_considerations: {
    warning_time_seconds: 10,
    evasion_possible: 'LIMITED',
    hardened_positions_effective: true,
    counter_uas_effective: 'VARIABLE'
  }
};

// ============================================================================
// MEDICAL COUNTER-UAS CONSIDERATIONS (NEW in v2.0)
// ============================================================================

export const MEDICAL_COUNTER_UAS = {
  description: 'Counter-UAS measures for medical units',
  
  passive_measures: {
    camouflage: { effectiveness: 0.30, cost: 'LOW' },
    dispersion: { effectiveness: 0.40, cost: 'LOW' },
    noise_discipline: { effectiveness: 0.20, cost: 'NONE' },
    thermal_reduction: { effectiveness: 0.35, cost: 'MODERATE' },
    underground_positions: { effectiveness: 0.80, cost: 'HIGH' },
    night_operations: { effectiveness: 0.50, cost: 'MODERATE' }
  },
  
  active_measures: {
    ew_jamming: { 
      effectiveness: 0.60, 
      cost: 'HIGH',
      medical_safe: true,
      affects_own_comms: true
    },
    kinetic_cuas: {
      effectiveness: 0.40,
      cost: 'HIGH',
      medical_safe: false,  // Secondary fragmentation risk
      noise_signature: 'HIGH'
    },
    drone_nets: {
      effectiveness: 0.30,
      cost: 'MODERATE',
      medical_safe: true
    }
  },
  
  mtf_specific_recommendations: {
    ROLE_1: ['Camouflage', 'Dispersion', 'Rapid displacement'],
    ROLE_2: ['Underground if possible', 'EW protection', 'Night operations'],
    ROLE_3: ['Hardened structure', 'Active CUAS', 'Dispersion of annexes']
  },
  
  ambulance_protection: {
    armored_cab: { protection: 'FPV_FRAGMENTS', weight_kg: 300 },
    ew_jammer_vehicle: { effectiveness: 0.50, range_m: 500 },
    drone_detection: { warning_time_s: 15 },
    tactical_procedures: [
      'Avoid predictable routes',
      'Night movement preferred',
      'Covered loading areas',
      'Buddy system with cover vehicle'
    ]
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get combined threat modifiers for casualty calculations
 * @param {Object} threatConfig - Selected threat configuration
 * @returns {Object} Combined modifiers
 */
export const getThreatModifiers = (threatConfig) => {
  const {
    threat_type = 'CONVENTIONAL',
    enemy_ad = 'NONE',
    ied_threat = 'NONE',
    insurgent_activity = 'NONE',
    cbrn_risk = 'NONE',
    artillery_threat = 'LOW',
    drone_threat = 'NONE'
  } = threatConfig;
  
  const threatType = THREAT_TYPES[threat_type] || THREAT_TYPES.CONVENTIONAL;
  const ad = ENEMY_AD_LEVELS[enemy_ad] || ENEMY_AD_LEVELS.NONE;
  const ied = IED_THREAT_LEVELS[ied_threat] || IED_THREAT_LEVELS.NONE;
  const insurgent = INSURGENT_ACTIVITY[insurgent_activity] || INSURGENT_ACTIVITY.NONE;
  const cbrn = CBRN_RISK[cbrn_risk] || CBRN_RISK.NONE;
  const artillery = ARTILLERY_THREAT[artillery_threat] || ARTILLERY_THREAT.LOW;
  const drone = DRONE_THREAT[drone_threat] || DRONE_THREAT.NONE;
  
  // Calculate combined casualty modifier
  const casualtyModifier = 
    threatType.casualty_modifier *
    ied.casualty_modifier *
    insurgent.rear_area_casualty_modifier *
    artillery.casualty_modifier *
    drone.casualty_modifier;
  
  // Calculate DOW modifier
  const dowModifier = Math.max(
    ad.dow_modifier || 1.0,
    ied.dow_modifier || 1.0,
    cbrn.dow_modifier || 1.0,
    drone.dow_modifier || 1.0
  );
  
  // Calculate evacuation speed modifier
  const groundEvacModifier = 
    ied.ground_evac_speed_modifier *
    (threatType.evacuation_factors?.ground_evac_risk_modifier || 1.0) *
    insurgent.loc_security_modifier;
  
  // MEDEVAC availability
  const medevacAvailability = ad.medevac_availability;
  const goldenHourAchievable = ad.golden_hour_achievable && ied.value !== 'EXTREME';
  
  return {
    // Primary modifiers for calculator
    casualty_modifier: Math.round(casualtyModifier * 100) / 100,
    dow_modifier: Math.round(dowModifier * 100) / 100,
    ground_evac_modifier: Math.round(groundEvacModifier * 100) / 100,
    medevac_availability: medevacAvailability,
    golden_hour_achievable: goldenHourAchievable,
    
    // DNBI modifier
    dnbi_modifier: threatType.dnbi_modifier * (cbrn.cbrn_casualty_addition > 0 ? 1.2 : 1.0),
    
    // Wound patterns (for surgical planning)
    wound_patterns: threatType.wound_patterns,
    
    // Evacuation delays
    evacuation_delay_minutes: 
      (ied.evacuation_delay_minutes || 0) +
      (artillery.value === 'EXTREME' ? 20 : artillery.value === 'HIGH' ? 10 : 0),
    
    // Asset requirements
    requirements: {
      armored_ambulances: ied.armored_ambulance_required || drone.value === 'SATURATED',
      forward_surgical: ad.forward_surgical_required || artillery.forward_surgical_critical,
      route_clearance: ied.route_clearance_required,
      convoy_escort: insurgent.convoy_escort_required,
      decontamination: cbrn.decontamination_required,
      counter_uas: drone.counter_uas_required,
      mtf_mobility: artillery.mtf_mobility_required
    },
    
    // Warnings for planning
    warnings: generateThreatWarnings(threatConfig)
  };
};

/**
 * Generate warning messages based on threat configuration
 */
const generateThreatWarnings = (threatConfig) => {
  const warnings = [];
  
  const ad = ENEMY_AD_LEVELS[threatConfig.enemy_ad];
  const ied = IED_THREAT_LEVELS[threatConfig.ied_threat];
  const cbrn = CBRN_RISK[threatConfig.cbrn_risk];
  const artillery = ARTILLERY_THREAT[threatConfig.artillery_threat];
  const drone = DRONE_THREAT[threatConfig.drone_threat];
  
  if (ad?.value === 'DENSE' || ad?.value === 'SOPHISTICATED') {
    warnings.push({
      severity: 'error',
      category: 'MEDEVAC',
      message: 'MEDEVAC severely restricted or unavailable. Plan for ground evacuation.',
      recommendations: ['Deploy forward surgical teams', 'Increase ground ambulances', 'Consider Role 2E forward']
    });
  }
  
  if (ied?.value === 'HIGH' || ied?.value === 'EXTREME') {
    warnings.push({
      severity: 'error',
      category: 'IED',
      message: 'High IED threat significantly impacts ground evacuation.',
      recommendations: ['Armored ambulances required', 'Route clearance mandatory', 'Expect evacuation delays', 'Blast injury surgical capability']
    });
  }
  
  if (cbrn?.value === 'MODERATE' || cbrn?.value === 'HIGH') {
    warnings.push({
      severity: 'warning',
      category: 'CBRN',
      message: 'CBRN threat reduces medical capacity and increases treatment times.',
      recommendations: ['Decontamination stations at MTFs', 'Antidote stocks required', 'Collective protection', 'Clean/dirty separation']
    });
  }
  
  if (artillery?.value === 'HIGH' || artillery?.value === 'EXTREME') {
    warnings.push({
      severity: 'error',
      category: 'ARTILLERY',
      message: 'High artillery threat to all echelons including MTFs.',
      recommendations: ['MTF dispersion and mobility', 'Signature reduction', 'Plan for MASCAL events', 'Underground protection if possible']
    });
  }
  
  if (drone?.value === 'CONTESTED' || drone?.value === 'SATURATED') {
    warnings.push({
      severity: 'warning',
      category: 'DRONE',
      message: 'Drone threat to medical vehicles and positions.',
      recommendations: ['Counter-UAS capability', 'Armored/covered evacuation', 'Night movement emphasis', 'Walking wounded procedures']
    });
  }
  
  return warnings;
};

/**
 * Get threat-adjusted KIA/WIA distribution
 */
export const getAdjustedCasualtyDistribution = (threatType, iedThreat) => {
  const baseDistribution = THREAT_TYPES[threatType]?.wia_distribution || 
    THREAT_TYPES.CONVENTIONAL.wia_distribution;
  
  const iedAdjustment = IED_THREAT_LEVELS[iedThreat]?.wound_pattern_adjustment || { blast: 0, penetrating: 0 };
  
  // Adjust wound patterns based on IED threat
  const woundPatterns = { ...THREAT_TYPES[threatType]?.wound_patterns || THREAT_TYPES.CONVENTIONAL.wound_patterns };
  woundPatterns.blast = Math.min(0.70, Math.max(0.10, woundPatterns.blast + iedAdjustment.blast));
  woundPatterns.penetrating = Math.min(0.60, Math.max(0.10, woundPatterns.penetrating + iedAdjustment.penetrating));
  
  // Normalize
  const total = Object.values(woundPatterns).reduce((a, b) => a + b, 0);
  Object.keys(woundPatterns).forEach(key => {
    woundPatterns[key] = Math.round((woundPatterns[key] / total) * 100) / 100;
  });
  
  return {
    kia_ratio: THREAT_TYPES[threatType]?.kia_ratio || 0.20,
    wia_distribution: baseDistribution,
    wound_patterns: woundPatterns
  };
};

/**
 * Validate threat configuration for consistency
 */
export const validateThreatConfiguration = (threatConfig) => {
  const errors = [];
  const warnings = [];
  
  // Check for inconsistent combinations
  if (threatConfig.threat_type === 'PEACEKEEPING' && threatConfig.enemy_ad !== 'NONE') {
    warnings.push('Peace Support Operations typically have no enemy AD threat');
  }
  
  if (threatConfig.threat_type === 'CONVENTIONAL' && threatConfig.ied_threat === 'EXTREME') {
    warnings.push('Extreme IED threat unusual in conventional warfare - consider ASYMMETRIC or HYBRID');
  }
  
  if (threatConfig.enemy_ad === 'SOPHISTICATED' && threatConfig.drone_threat === 'NONE') {
    warnings.push('Sophisticated AD typically includes drone capability');
  }
  
  return { errors, warnings, isValid: errors.length === 0 };
};

// ============================================================================
// DRONE THREAT BY DAY (planner input, Dr. Tatar 2026-10-10)
// ============================================================================

/**
 * Drone threat that applies on a given day. The planner sets a scenario-wide level (drone_threat) and optionally a
 * DOW multiplier of their own (drone_dow_modifier); `drone_schedule` lists changes by day:
 *   [{ from_day: 4, drone_threat: 'SATURATED', dow_modifier: 1.3 }, ...]
 * An entry applies from its day until the next entry. An entry that changes the level but gives no dow_modifier
 * returns to that level's default multiplier (it does not inherit an earlier override).
 * Throws on an unknown level, a non-integer day, or a multiplier outside 1.0 to 3.0.
 *
 * @returns {{level:string, data:Object, dowModifier:number, source:'PLANNER'|'LEVEL_DEFAULT'}}
 */
export const DRONE_DOW_MODIFIER_RANGE = Object.freeze({ min: 1.0, max: 3.0 });

export const resolveDroneForDay = (threatAssessment, day) => {
  const ta = threatAssessment || {};
  const checkLevel = (lvl, where) => {
    if (!Object.prototype.hasOwnProperty.call(DRONE_THREAT, lvl)) {
      throw new Error(`resolveDroneForDay: unknown drone threat '${lvl}' in ${where} (valid: ${Object.keys(DRONE_THREAT).join(', ')})`);
    }
  };
  const checkModifier = (m, where) => {
    if (typeof m !== 'number' || Number.isNaN(m) || m < DRONE_DOW_MODIFIER_RANGE.min || m > DRONE_DOW_MODIFIER_RANGE.max) {
      throw new Error(`resolveDroneForDay: drone DOW multiplier in ${where} must be a number from ${DRONE_DOW_MODIFIER_RANGE.min} to ${DRONE_DOW_MODIFIER_RANGE.max} (got: ${m})`);
    }
  };
  let level = ta.drone_threat ?? 'NONE';
  checkLevel(level, 'drone_threat');
  let override = ta.drone_dow_modifier ?? null;
  if (override !== null) checkModifier(override, 'drone_dow_modifier');

  const schedule = ta.drone_schedule ?? [];
  if (!Array.isArray(schedule)) throw new Error('resolveDroneForDay: drone_schedule must be an array');
  schedule.forEach((e, i) => {
    if (!e || !Number.isInteger(e.from_day) || e.from_day < 1) {
      throw new Error(`resolveDroneForDay: drone_schedule[${i}].from_day must be a whole day >= 1 (got: ${e && e.from_day})`);
    }
    if (e.drone_threat !== undefined && e.drone_threat !== null) checkLevel(e.drone_threat, `drone_schedule[${i}]`);
    if (e.dow_modifier !== undefined && e.dow_modifier !== null) checkModifier(e.dow_modifier, `drone_schedule[${i}]`);
  });
  [...schedule].sort((a, b) => a.from_day - b.from_day).filter((e) => e.from_day <= day).forEach((e) => {
    if (e.drone_threat) {
      level = e.drone_threat;
      override = null;
    }
    if (e.dow_modifier !== undefined && e.dow_modifier !== null) override = e.dow_modifier;
  });

  const data = DRONE_THREAT[level];
  const levelDefault = data.dow_modifier;
  if (typeof levelDefault !== 'number') {
    throw new Error(`resolveDroneForDay: DRONE_THREAT.${level}.dow_modifier is not defined in threatAssessment.js`);
  }
  return {
    level,
    data,
    dowModifier: override !== null ? override : levelDefault,
    source: override !== null ? 'PLANNER' : 'LEVEL_DEFAULT'
  };
};

// ============================================================================
// DEFAULT CONFIGURATION
// ============================================================================

export const DEFAULT_THREAT_CONFIG = {
  threat_type: 'CONVENTIONAL',
  enemy_ad: 'LIMITED',
  ied_threat: 'LOW',
  insurgent_activity: 'NONE',
  cbrn_risk: 'NONE',
  artillery_threat: 'MODERATE',
  drone_threat: 'LIMITED_FPV'
};

// Ukraine 2024 baseline for high-intensity planning
export const UKRAINE_2024_BASELINE = {
  threat_type: 'HIGH_INTENSITY',
  enemy_ad: 'SOPHISTICATED',
  ied_threat: 'MODERATE',
  insurgent_activity: 'NONE',
  cbrn_risk: 'LOW',
  artillery_threat: 'EXTREME',
  drone_threat: 'SATURATED'
};

// ============================================================================
// ENHANCED HELPER FUNCTIONS (v2.0)
// ============================================================================

/**
 * Get FPV-adjusted injury distribution for surgical planning
 */
export const getFPVAdjustedInjuryDistribution = (droneThreatLevel, baseMechanism = 'ARTILLERY') => {
  const fpvData = FPV_INJURY_PATTERNS;
  const droneLevel = DRONE_THREAT[droneThreatLevel];
  
  if (!droneLevel || droneThreatLevel === 'NONE') {
    // Return standard distribution
    return fpvData.artillery_body_region;
  }
  
  // Calculate FPV percentage based on threat level
  let fpvPercentage = 0;
  switch(droneThreatLevel) {
    case 'LIMITED_FPV': fpvPercentage = 0.15; break;
    case 'CONTESTED': fpvPercentage = 0.25; break;
    case 'SATURATED': fpvPercentage = 0.35; break;
    default: fpvPercentage = 0;
  }
  
  // Blend distributions
  const blended = {};
  Object.keys(fpvData.body_region_distribution).forEach(region => {
    const fpvRate = fpvData.body_region_distribution[region];
    const artRate = fpvData.artillery_body_region[region];
    blended[region] = Math.round((fpvRate * fpvPercentage + artRate * (1 - fpvPercentage)) * 100) / 100;
  });
  
  return blended;
};

/**
 * Get specialty surgical demand modifier based on drone threat
 */
export const getDroneSpecialtySurgicalModifier = (droneThreatLevel) => {
  if (droneThreatLevel === 'NONE' || droneThreatLevel === 'RECONNAISSANCE_ONLY') {
    return {
      neurosurgery: 1.0,
      maxillofacial: 1.0,
      ophthalmology: 1.0,
      ent: 1.0
    };
  }
  
  let modifier = 1.0;
  switch(droneThreatLevel) {
    case 'LIMITED_FPV': modifier = 1.15; break;
    case 'CONTESTED': modifier = 1.30; break;
    case 'SATURATED': modifier = 1.50; break;
    default: modifier = 1.0; break;  // No drone threat or unknown level
  }
  
  return {
    neurosurgery: modifier * 1.2,    // Extra increase
    maxillofacial: modifier * 1.3,   // Highest increase
    ophthalmology: modifier * 1.25,
    ent: modifier * 1.1
  };
};

/**
 * Calculate combined threat score (0-100)
 */
export const calculateThreatScore = (threatConfig) => {
  const weights = {
    threat_type: 25,
    enemy_ad: 20,
    artillery_threat: 20,
    drone_threat: 15,
    ied_threat: 10,
    cbrn_risk: 5,
    insurgent_activity: 5
  };
  
  const scores = {
    threat_type: {
      PEACEKEEPING: 10, ASYMMETRIC: 40, CONVENTIONAL: 60, HYBRID: 70, HIGH_INTENSITY: 100
    },
    enemy_ad: {
      NONE: 0, LIMITED: 25, MODERATE: 50, DENSE: 75, SOPHISTICATED: 100
    },
    artillery_threat: {
      LOW: 10, MODERATE: 40, HIGH: 70, EXTREME: 100
    },
    drone_threat: {
      NONE: 0, RECONNAISSANCE_ONLY: 20, LIMITED_FPV: 40, CONTESTED: 70, SATURATED: 100
    },
    ied_threat: {
      NONE: 0, LOW: 20, MODERATE: 50, HIGH: 75, EXTREME: 100
    },
    cbrn_risk: {
      NONE: 0, LOW: 25, MODERATE: 60, HIGH: 100
    },
    insurgent_activity: {
      NONE: 0, LOW: 25, MODERATE: 50, HIGH: 75, EXTREME: 100
    }
  };
  
  let totalScore = 0;
  Object.keys(weights).forEach(key => {
    const value = threatConfig[key] || Object.keys(scores[key])[0];
    const score = scores[key][value] || 0;
    totalScore += (score * weights[key]) / 100;
  });
  
  return Math.round(totalScore);
};

/**
 * Get threat level descriptor
 */
export const getThreatLevelDescriptor = (score) => {
  if (score >= 80) return { level: 'CRITICAL', color: 'error', description: 'Extreme threat environment' };
  if (score >= 60) return { level: 'HIGH', color: 'error', description: 'High threat environment' };
  if (score >= 40) return { level: 'MODERATE', color: 'warning', description: 'Moderate threat environment' };
  if (score >= 20) return { level: 'LOW', color: 'info', description: 'Low threat environment' };
  return { level: 'MINIMAL', color: 'success', description: 'Minimal threat environment' };
};

/**
 * Get evacuation recommendations based on threat
 */
export const getEvacuationRecommendations = (threatConfig) => {
  const recommendations = {
    primary_mode: 'GROUND',
    backup_mode: null,
    required_assets: [],
    time_multiplier: 1.0,
    golden_hour_achievable: true,
    warnings: []
  };
  
  const ad = ENEMY_AD_LEVELS[threatConfig.enemy_ad];
  const ied = IED_THREAT_LEVELS[threatConfig.ied_threat];
  const drone = DRONE_THREAT[threatConfig.drone_threat];
  
  // MEDEVAC availability
  if (ad?.medevac_availability >= 0.7) {
    recommendations.primary_mode = 'MEDEVAC';
    recommendations.backup_mode = 'GROUND';
  } else {
    recommendations.primary_mode = 'GROUND';
    if (ad?.medevac_availability >= 0.3) {
      recommendations.backup_mode = 'MEDEVAC_EMERGENCY';
    }
  }
  
  // Required assets
  if (ied?.armored_ambulance_required) {
    recommendations.required_assets.push('ARMORED_AMBULANCES');
  }
  if (drone?.counter_uas_required) {
    recommendations.required_assets.push('COUNTER_UAS');
  }
  if (ied?.route_clearance_required) {
    recommendations.required_assets.push('ROUTE_CLEARANCE');
  }
  
  // Time multiplier
  recommendations.time_multiplier = 
    (ied?.ground_evac_speed_modifier || 1.0) *
    (drone?.evacuation_by_vehicle_risk || 1.0);
  
  // Golden hour
  recommendations.golden_hour_achievable = 
    ad?.golden_hour_achievable !== false &&
    recommendations.time_multiplier < 1.5;
  
  return recommendations;
};

/**
 * Get current quarter drone percentage projection
 */
export const getProjectedDronePercentage = (year, quarter) => {
  const key = `${year}_Q${quarter}`;
  const trend = FPV_INJURY_PATTERNS.quarterly_trends[key];
  
  if (trend) {
    return trend.fpv_percentage;
  }
  
  // Project forward if beyond available data
  const lastKnown = FPV_INJURY_PATTERNS.quarterly_trends['2024_Q4'];
  const monthsPast = ((year - 2024) * 12) + ((quarter - 4) * 3);
  const projectedIncrease = monthsPast * 0.01;  // 1% per month growth
  
  return Math.min(0.50, lastKnown.fpv_percentage + projectedIncrease);
};

// ============================================================================
// EXPORT ALL
// ============================================================================

// Named default export (fixes lint warning)
const threatAssessmentConstants = {
  THREAT_TYPES,
  ENEMY_AD_LEVELS,
  IED_THREAT_LEVELS,
  INSURGENT_ACTIVITY,
  CBRN_RISK,
  ARTILLERY_THREAT,
  DRONE_THREAT,
  FPV_INJURY_PATTERNS,
  LOITERING_MUNITIONS,
  MEDICAL_COUNTER_UAS,
  getThreatModifiers,
  getAdjustedCasualtyDistribution,
  validateThreatConfiguration,
  getFPVAdjustedInjuryDistribution,
  getDroneSpecialtySurgicalModifier,
  calculateThreatScore,
  getThreatLevelDescriptor,
  getEvacuationRecommendations,
  getProjectedDronePercentage,
  DEFAULT_THREAT_CONFIG,
  UKRAINE_2024_BASELINE
};

export default threatAssessmentConstants;
